import React from 'react';
import { Check } from 'lucide-react';
import { cn } from 'lib/utils';

export interface AccessDecisionOption {
  value: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface AccessDecisionCardsProps {
  options: AccessDecisionOption[];
  value: string;
  onChange: (value: string) => void;
  /** Optional visually-hidden group label — the page already carries a heading. */
  legend?: string;
}

/** A radio set for keyboard + a11y; selecting sets the same `accessType` values the save logic expects. */
export const AccessDecisionCards: React.FC<AccessDecisionCardsProps> = ({ options, value, onChange, legend }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" role="radiogroup" aria-label={legend}>
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(opt.value)}
          className={cn(
            'flex flex-col items-start gap-3 rounded-lg border bg-card p-4 text-left transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            selected
              ? 'border-foreground ring-1 ring-foreground'
              : 'border-border hover:border-foreground/20 hover:bg-muted/40',
          )}
        >
          <div className="flex w-full items-start justify-between">
            <span
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md',
                selected ? 'bg-muted text-foreground' : 'bg-muted text-muted-foreground',
              )}
            >
              {opt.icon}
            </span>
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
                selected ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/30',
              )}
              aria-hidden
            >
              {selected && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
          </div>
          <span className="text-sm font-semibold text-foreground">{opt.title}</span>
          <span className="text-xs leading-relaxed text-muted-foreground">{opt.description}</span>
        </button>
      );
    })}
  </div>
);

export default AccessDecisionCards;
